import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import PolicyIcon from '@mui/icons-material/Policy';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import GavelIcon from '@mui/icons-material/Gavel';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import { getTeam } from '../api/runs';
import type { FeedEvent, FeedEventType, MultilingualString, Team } from '../api/types';
import { resolveLocalized } from '../i18n/contentLocales';
import { useWebSocket } from '../hooks/useWebSocket';
import { runStateToastKey } from './runStateToast';
import { useParticipantRunState } from '../hooks/useParticipantRunState';
import { useAuth } from '../auth/useAuth';
import OfflineBanner from '../components/OfflineBanner';
import ParticipantProfileSheet from '../components/ParticipantProfileSheet';
import ParticipantBottomNav from '../components/ParticipantBottomNav';
import {
  BOTTOM_NAV_HEIGHT,
  rememberLastRun,
  type ParticipantTab,
} from '../components/participantNav';
import logoSmall from '../assets/logos/logo-small.svg';

const HEADER_HEIGHT = 56;

const SUPPORTED_LANGS = ['de', 'en'] as const;

/** The four kinds the coordination can push at a run; everything else on the
 *  feed narrates what participants themselves did. */
const COORDINATION_NOTICE_TYPES: FeedEventType[] = ['announcement', 'countdown', 'reminder', 'alert'];

const RunShell: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation('runs');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  // A notice pushed by the coordination. Kept apart from the run-state toast so
  // an alert can stay on screen while a state change comes and goes.
  const [notice, setNotice] = useState<{ text: string; type: FeedEventType } | null>(null);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const [languageAnchor, setLanguageAnchor] = useState<HTMLElement | null>(null);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // #242 — participant-scoped, read-only run state: the lifecycle is read via
  // the membership-bound GET /api/public/runs/{id}/state (no coordinator 403),
  // and run_state_change arrives over the guest-authorized run WS. This drives
  // the run name in the header, the state-change toast, and the end-of-run
  // redirect below — without ever calling an org/coordinator run endpoint.
  const { run, loading } = useParticipantRunState({
    orgSlug: user?.organization_slug,
    runId,
    onStateChange: (next, previous) => {
      setToastMessage(t(`participant.runStateChange.${runStateToastKey(next, previous)}`));
      if (next === 'ended' && runId) {
        // Give the participant ~2s to read the toast, then send them to feedback.
        // (Phase 2.4 will replace this with an opt-in celebratory + feedback prompt sequence.)
        window.setTimeout(() => {
          navigate(`/runs/${runId}/participant/feedback`);
        }, 2000);
      }
    },
  });

  // #488 — an announcement, countdown, reminder or alert reached only whoever
  // happened to be sitting on the activity tab, because that view was the sole
  // place handling feed_event. An alert nobody sees is not an alert. The run WS
  // is already here for run_state_change; the notices ride the same channel and
  // land on the same surface as "the run was paused".
  useWebSocket({
    path: user?.organization_slug && runId ? `/api/orgs/${user.organization_slug}/runs/${runId}/ws` : '',
    enabled: !!(user?.organization_slug && runId),
    handlers: {
      feed_event: (data) => {
        const event = data as FeedEvent;
        // System entries narrate what the participant just did — surfacing them
        // as a notice would be noise on top of the action itself.
        if (!COORDINATION_NOTICE_TYPES.includes(event.event_type)) return;
        const text = resolveLocalized(event.content.text as MultilingualString, i18n.language).text;
        if (text) setNotice({ text, type: event.event_type });
      },
    },
  });

  // Spec 023 — load the user's team so the share-code can be shown in the header.
  // team_id is set by GuestJoin in sessionStorage on successful join.
  const [team, setTeam] = useState<Team | null>(null);
  useEffect(() => {
    if (!user?.organization_slug || !runId) return;
    const teamId = sessionStorage.getItem(`clavis_team_for_run_${runId}`);
    if (!teamId) return;
    let cancelled = false;
    getTeam(user.organization_slug, runId, teamId)
      .then((t) => { if (!cancelled) setTeam(t); })
      .catch(() => { /* swallow — header just won't show the chip */ });
    return () => { cancelled = true; };
  }, [user?.organization_slug, runId]);

  // Remember the run so run-less pages (history, legal) can route the bottom
  // nav back into it.
  useEffect(() => {
    if (runId) rememberLastRun(runId);
  }, [runId]);

  // Determine active bottom-nav tab from the URL.
  const active: ParticipantTab = (() => {
    if (location.pathname.includes('/team')) return 'team';
    if (location.pathname.includes('/feed')) return 'feed';
    return 'rallye';
  })();

  const openOverflow = (e: React.MouseEvent<HTMLElement>) => setOverflowAnchor(e.currentTarget);
  const closeOverflow = () => setOverflowAnchor(null);
  const closeLanguage = () => setLanguageAnchor(null);

  const handleProfile = () => {
    closeOverflow();
    setProfileOpen(true);
  };

  const handleLanguage = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setLanguageAnchor(e.currentTarget);
  };

  const handlePickLanguage = (lang: string) => {
    void i18n.changeLanguage(lang);
    closeLanguage();
    closeOverflow();
  };

  const goLegal = (path: string) => () => {
    closeOverflow();
    navigate(path);
  };

  const handleLogout = async () => {
    closeOverflow();
    await logout();
    navigate('/login');
  };

  // Header shows the run/rallye name (the primary identity for the user
  // inside the run). When loading or unavailable, fall back to the brand
  // wordmark so the bar never reads as the bottom-nav tab label.
  const headerPrimary = run?.name ?? 'Stadtrallye';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        bgcolor: 'background.default',
      }}
    >
      <a href="#main-content" className="skip-link">{tCommon('aria.skipToMain')}</a>
      <AppBar position="fixed" elevation={1} sx={{ top: 0 }}>
        <Toolbar
          variant="dense"
          disableGutters
          sx={{
            minHeight: HEADER_HEIGHT,
            px: 2,
            gap: 1.25,
            maxWidth: 720,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Box
            component="img"
            src={logoSmall}
            alt="Stadtrallye"
            sx={{ width: 28, height: 24, flexShrink: 0 }}
          />
          <Typography
            variant="subtitle1"
            component="div"
            noWrap
            sx={{ flex: 1, minWidth: 0, lineHeight: 1.2, fontWeight: 600 }}
          >
            {headerPrimary}
          </Typography>
          {/* #259: the team share-code is intentionally not shown in the run
              header during play — it stays available in the Team tab. */}
          <Tooltip title={t('participant.overflow.menuLabel')}>
            <IconButton
              size="small"
              edge="end"
              color="inherit"
              aria-label={t('participant.overflow.menuLabel')}
              aria-controls="run-overflow-menu"
              aria-haspopup="true"
              onClick={openOverflow}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
        <OfflineBanner />
      </AppBar>

      <Menu
        id="run-overflow-menu"
        anchorEl={overflowAnchor}
        open={Boolean(overflowAnchor)}
        onClose={closeOverflow}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { closeOverflow(); navigate('/run-history'); }}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.runHistory')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLanguage}>
          <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.language')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={goLegal('/legal/privacy')}>
          <ListItemIcon><PolicyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.dataPrivacy')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={goLegal('/legal/accessibility')}>
          <ListItemIcon><AccessibilityNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.accessibility')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={goLegal('/legal/impressum')}>
          <ListItemIcon><GavelIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.impressum')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('participant.overflow.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageAnchor}
        open={Boolean(languageAnchor)}
        onClose={closeLanguage}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SUPPORTED_LANGS.map((lang) => {
          const active = i18n.language === lang || i18n.language?.startsWith(`${lang}-`);
          return (
            <MenuItem key={lang} onClick={() => handlePickLanguage(lang)} selected={active}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {active ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={tCommon(`language.names.${lang}`)}
                secondary={tCommon(`language.${lang}`)}
              />
            </MenuItem>
          );
        })}
      </Menu>

      <Box
        component="main"
        id="main-content"
        sx={{
          flex: 1,
          pt: `${HEADER_HEIGHT + 8}px`,
          pb: `${BOTTOM_NAV_HEIGHT + 8}px`,
          px: 2,
          maxWidth: 720,
          mx: 'auto',
          width: '100%',
        }}
      >
        {loading && !run ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Outlet context={{ run, team }} />
        )}
      </Box>

      <ParticipantBottomNav runId={runId ?? null} active={active} />

      <ParticipantProfileSheet
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      <Snackbar
        open={!!notice}
        // An alert waits to be acknowledged; the softer kinds fade on their own.
        autoHideDuration={notice?.type === 'alert' ? null : 8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: `${HEADER_HEIGHT + 8}px`, sm: `${HEADER_HEIGHT + 8}px` } }}
      >
        <Alert
          onClose={() => setNotice(null)}
          severity={notice?.type === 'alert' ? 'error' : 'info'}
          variant="filled"
        >
          {notice?.text}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!toastMessage && !notice}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: `${HEADER_HEIGHT + 8}px`, sm: `${HEADER_HEIGHT + 8}px` } }}
      >
        <Alert
          onClose={() => setToastMessage(null)}
          severity={run?.state === 'ended' ? 'warning' : 'info'}
          variant="filled"
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={3000}
        onClose={() => setComingSoonOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: `${BOTTOM_NAV_HEIGHT + 16}px` }}
      >
        <Alert severity="info" variant="filled" onClose={() => setComingSoonOpen(false)}>
          {t('participant.overflow.comingSoon')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RunShell;
